import { getStripeCard, getToken, parseBilling } from '../utils/stripe';

let module = angular.module('app.services.checkout', []);

export class CheckoutService {
  constructor($window, $timeout, $q, $http, Account) {
    'ngInject';

    // Various SKU constants
    this.BOXCASTER_SKU = 'BXC-HDSD2';
    this.BOXCASTER_PRO_SKU = 'BXC-PRO-2995';
    this.RETURN_LABEL_SKU = 'shipping-return-label';
    this.SHURE_KIT_SKU = 'BXC-AUDIO-IOS';
    this.SHURE_COUPON_SKU = 'coupon:one-time:promotion-bxc-audio-ios:28800';

    // PD Template IDs
    this.PANDADOC_TEMPLATE_FREE_TRIAL = 'WAbYt2SgSbviDpwSfzdJpe';
    this.PANDADOC_TEMPLATE_FREE_TRIAL_WITH_PROMO = 'YE6QEWNPK3UvsZABvyTQkJ';
    this.$window = $window;
    this.$timeout = $timeout;
    this.$q = $q;
    this.$http = $http;
    this.Account = Account;
  }

  registerPandadocWatcher($scope, handler) {
    const { $window } = this;

    const onMessage = (evt) => {
      if (evt && evt.data) {
        try {
          var message = JSON.parse(evt.data);
          if (message && message.event) {
            onHandlePandadocMessage(message.event, message.data);
          }
        } catch (e) {
          console.warn(e);
        }
      }
    };

    const onHandlePandadocMessage = (event, data) => {
      $scope.$evalAsync(() => {
        handler && handler(event, data);
      });
    };

    $window.addEventListener('message', onMessage, false);
    $scope.$on('$destroy', () => {
      $window.removeEventListener('message', onMessage);
    });
  }

  openPlaidLink() {
    let deferred = this.$q.defer();
    Plaid.create({
      env: PLAID_ENV,
      key: PLAID_KEY,
      apiVersion: 'v2',
      clientName: 'BoxCast',
      product: ['auth'],
      selectAccount: true,
      onSuccess: (public_token, metadata) => {
        this.$http
          .post(`${INTEGRATIONS_ROOT}/plaid/exchange_token`, {
            public_token: public_token,
            plaid_account_id: metadata.account_id,
          })
          .then((response) => {
            deferred.resolve({
              metadata: metadata,
              tokenResponse: response,
            });
          })
          .catch((err) => {
            deferred.reject(err);
          });
      },
    }).open();
    return deferred.promise;
  }

  submitCard(stripeCard) {
    let deferred = this.$q.defer();
    getToken(stripeCard)
      .then((token) => {
        this.$timeout(() => {
          deferred.resolve(token);
        });
      })
      .catch((err) => {
        this.$timeout(() => {
          deferred.reject(err);
        });
      });
    return deferred.promise;
  }

  getLocations() {
    let deferred = this.$q.defer();
    let promises = [];
    this.states = [];
    this.countries = [];

    promises.push(
      this.$http.get('/img/states.json').then((response) => {
        this.states = response.data;
      })
    );
    promises.push(
      this.$http.get('/img/countries.json').then((response) => {
        this.countries = response.data;
      })
    );
    this.$q.all(promises).then(() => {
      deferred.resolve({
        states: this.states,
        countries: this.countries,
      });
    });
    return deferred.promise;
  }

  getBilling() {
    var deferred = this.$q.defer();
    this.Account.getBilling()
      .$promise.then((data) => {
        deferred.resolve(parseBilling(data || {}));
      })
      .catch((err) => {
        if (err.status == 404) {
          // No linked Stripe customer
          deferred.resolve({}, null, null);
        } else {
          deferred.reject(err);
        }
      });
    return deferred.promise;
  }

  isPurchasingSubscription(proposal) {
    return proposal.products.subscription_items.length > 0;
  }

  validateBillingAddress(billing) {
    if (!billing) return false;
    return (
      !!billing.address &&
      !!billing.city &&
      !!billing.state &&
      !!billing.country &&
      !!billing.postal_code
    );
  }

  validateShippingAddress(shipping) {
    return (
      !!shipping.address &&
      !!shipping.city &&
      !!shipping.state &&
      !!shipping.country &&
      !!shipping.postal_code
    );
  }

  isDocumentSelfServiceGenerated(currentUpgrade) {
    var pdMetadata = ((currentUpgrade.proposal || {}).document || {}).metadata || {};
    return isTruthy(pdMetadata.SelfService);
  }

  stripeBasePlanNameShortFromBillingResult(billingResult) {
    var name = '';
    if (!billingResult.subscriptions || !billingResult.subscriptions.data) {
      return name;
    }
    billingResult.subscriptions.data.forEach((sub) => {
      sub.items.data.forEach((subItem) => {
        if (subItem.plan.metadata.is_addon == 'false') {
          function planNameContains(str) {
            if (subItem.plan.id.toLowerCase().indexOf(str) >= 0) {
              return true;
            }
            if (
              ((subItem.plan.metadata || {}).display_name || '').toLowerCase().indexOf(str) >= 0
            ) {
              return true;
            }
            return false;
          }
          if (planNameContains('experience')) {
            name = 'Experience Plan';
          } else if (planNameContains('growth')) {
            name = 'Growth Plan';
          } else if (planNameContains('essential')) {
            name = 'Essential Plan';
          }
        }
      });
    });
    return name;
  }

  stripeBasePlanAmountFromBillingResult(billingResult) {
    let amount = 0;
    if (!billingResult.subscriptions || !billingResult.subscriptions.data) {
      return name;
    }
    billingResult.subscriptions.data.forEach((sub) => {
      sub.items.data.forEach((subItem) => {
        if (subItem.plan.metadata.is_addon == 'false') {
          amount = subItem.plan.amount / 100;
        }
      });
    });
    return amount;
  }

  stripeBasePlanLookupKeyFromBillingResult(billingResult) {
    let lookupkey = '';
    billingResult.subscriptions.data.forEach((sub) => {
      sub.items.data.forEach((subItem) => {
        if (subItem.plan.metadata.is_addon == 'false') {
          lookupkey = subItem.price.lookup_key;
        }
      });
    });
    return lookupkey;
  }

  stripePlanIDs(basePlan, interval, selectedAddOns) {
    let IDs = [];
    if (interval == 'annual') {
      IDs.push(`${basePlan}:${interval}`);
    } else if (basePlan == 'essential') {
      IDs.push('essential:monthly:9900');
    } else if (basePlan == 'growth') {
      IDs.push('growth:monthly:14900');
    } else if (basePlan == 'experience') {
      IDs.push('experience:monthly:19900');
    }
    IDs.push(...(selectedAddOns || []));
    return IDs;
  }

  stripeSKUs(encoder, oneTimeCharges, options = {}) {
    let res = [];
    res.push(...(oneTimeCharges || []));

    if (encoder == 'boxcaster') {
      res.push(this.BOXCASTER_SKU);
    } else if (encoder == 'pro') {
      res.push(this.BOXCASTER_PRO_SKU);
    }

    if (options.shure_promo) {
      res.push(this.SHURE_KIT_SKU);
      res.push(this.SHURE_COUPON_SKU);
    }

    return res;
  }

  paymentMethodFromAccount(account) {
    return _.find(['po', 'check', 'cc', 'ach'], (m) => account.payment_method == m) || 'cc';
  }

  currentInterval(billing) {
    const subData = (billing.subscriptions || {}).data || [];
    let interval = '';
    if (subData.length > 0 && (subData[0].items.data || []).length > 0) {
      interval = subData[0].items.data[0].plan.interval;
    }
    return interval;
  }
}

function isTruthy(metadataValue) {
  return metadataValue === true || metadataValue === 'true';
}

module.service('Checkout', CheckoutService);

export default module;



// WEBPACK FOOTER //
// ./app/services/checkout.js